import api from '../services/api'

export interface ImageUploadOptions {
    imageType?: 'avatar' | 'gig' | 'profile' | 'document' | 'other'
    imageName?: string
    referenceId?: string
    quality?: number
    maxWidth?: number
    maxHeight?: number
    forceFormat?: 'jpeg' | 'png' | 'webp'
}

export interface ImageUploadResponse {
    success: boolean
    data: {
        image: {
            _id: string
            imageType: string
            imageName: string
            mimeType: string
            size: number
            metadata: {
                width: number
                height: number
                format: string
                size: number
                quality?: number
            }
            dataUrl: string
            createdAt: string
        }
    }
    message: string
}

export interface MultipleImageUploadResponse {
    success: boolean
    data: {
        images: Array<ImageUploadResponse['data']['image']>
    }
    message: string
}

export interface UserStorageStats {
    totalImages: number
    totalSize: number
    byType: {
        [key: string]: {
            count: number
            size: number
        }
    }
}

class ImageService {
    private readonly MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

    /**
     * Upload single image file
     */
    async uploadImage(file: File, options: ImageUploadOptions = {}): Promise<ImageUploadResponse> {
        try {
            // Validate file size
            if (file.size > this.MAX_FILE_SIZE) {
                throw new Error(`File size exceeds 10MB limit`)
            }

            // Validate file type
            if (!file.type.startsWith('image/')) {
                throw new Error('Only image files are allowed')
            }

            const formData = new FormData()
            formData.append('image', file)

            // Add options to form data
            if (options.imageType) {
                formData.append('imageType', options.imageType)
            }
            if (options.imageName) {
                formData.append('imageName', options.imageName)
            }
            if (options.referenceId) {
                formData.append('referenceId', options.referenceId)
            }
            if (options.quality) {
                formData.append('quality', options.quality.toString())
            }
            if (options.maxWidth) {
                formData.append('maxWidth', options.maxWidth.toString())
            }
            if (options.maxHeight) {
                formData.append('maxHeight', options.maxHeight.toString())
            }
            if (options.forceFormat) {
                formData.append('forceFormat', options.forceFormat)
            }

            const response = await api.post<ImageUploadResponse>('/upload/image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            return response.data

        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || error.message || 'Image upload failed')
        }
    }

    /**
     * Upload multiple image files
     */
    async uploadMultipleImages(files: File[], options: ImageUploadOptions = {}): Promise<MultipleImageUploadResponse> {
        try {
            // Validate each file
            for (const file of files) {
                if (file.size > this.MAX_FILE_SIZE) {
                    throw new Error(`File ${file.name} exceeds 10MB limit`)
                }
                if (!file.type.startsWith('image/')) {
                    throw new Error(`File ${file.name} is not an image`)
                }
            }

            const formData = new FormData()

            // Add all files
            files.forEach(file => {
                formData.append('images', file)
            })

            // Add options to form data
            if (options.imageType) {
                formData.append('imageType', options.imageType)
            }
            if (options.referenceId) {
                formData.append('referenceId', options.referenceId)
            }
            if (options.quality) {
                formData.append('quality', options.quality.toString())
            }
            if (options.maxWidth) {
                formData.append('maxWidth', options.maxWidth.toString())
            }
            if (options.maxHeight) {
                formData.append('maxHeight', options.maxHeight.toString())
            }
            if (options.forceFormat) {
                formData.append('forceFormat', options.forceFormat)
            }

            const response = await api.post<MultipleImageUploadResponse>('/upload/images', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            return response.data

        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || error.message || 'Multiple image upload failed')
        }
    }

    /**
     * Get user's images
     */
    async getUserImages(imageType?: string, includeInactive = false): Promise<any[]> {
        try {
            const params = new URLSearchParams()

            if (imageType) {
                params.append('imageType', imageType)
            }
            if (includeInactive) {
                params.append('includeInactive', 'true')
            }

            const response = await api.get(`/upload?${params.toString()}`)
            return response.data.data.images

        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || 'Failed to fetch images')
        }
    }

    /**
     * Get image by ID
     */
    async getImageById(imageId: string): Promise<any> {
        try {
            const response = await api.get(`/upload/${imageId}`)
            return response.data.data.image

        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || 'Failed to fetch image')
        }
    }

    /**
     * Delete image
     */
    async deleteImage(imageId: string): Promise<void> {
        try {
            await api.delete(`/upload/${imageId}`)

        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || 'Failed to delete image')
        }
    }

    /**
     * Get user's current avatar
     */
    async getUserAvatar(): Promise<string | null> {
        try {
            const response = await api.get('/upload/avatar/current')
            return response.data.data.avatarUrl

        } catch (error: any) {
            // Avatar not found is not an error
            if (error.response?.status === 404) {
                return null
            }
            throw new Error(error.response?.data?.error?.message || 'Failed to fetch avatar')
        }
    }

    /**
     * Get gig images
     */
    async getGigImages(gigId: string): Promise<any[]> {
        try {
            const response = await api.get(`/upload/gig/${gigId}`)
            return response.data.data.images

        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || 'Failed to fetch gig images')
        }
    }

    /**
     * Get storage statistics
     */
    async getStorageStats(): Promise<UserStorageStats> {
        try {
            const response = await api.get('/upload/stats/storage')
            return response.data.data.stats

        } catch (error: any) {
            throw new Error(error.response?.data?.error?.message || 'Failed to fetch storage stats')
        }
    }

    /**
     * Upload avatar (convenience method)
     */
    async uploadAvatar(file: File, quality = 90, maxSize = 512): Promise<ImageUploadResponse> {
        return this.uploadImage(file, {
            imageType: 'avatar',
            imageName: `avatar_${Date.now()}`,
            quality,
            maxWidth: maxSize,
            maxHeight: maxSize,
            forceFormat: 'jpeg'
        })
    }

    /**
     * Upload gig images (convenience method)
     */
    async uploadGigImages(files: File[], gigId: string, quality = 85): Promise<MultipleImageUploadResponse> {
        return this.uploadMultipleImages(files, {
            imageType: 'gig',
            referenceId: gigId,
            quality,
            maxWidth: 1200,
            maxHeight: 900,
            forceFormat: 'jpeg'
        })
    }

    /**
     * Convert file to base64 for preview (client-side only, not used for storage)
     */
    fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = error => reject(error)
            reader.readAsDataURL(file)
        })
    }

    /**
     * Validate image file before upload
     */
    validateImageFile(file: File): { valid: boolean; error?: string } {
        // Check if it's an image
        if (!file.type.startsWith('image/')) {
            return { valid: false, error: 'File must be an image' }
        }

        // Check file size
        if (file.size > this.MAX_FILE_SIZE) {
            return { valid: false, error: 'File size must be less than 10MB' }
        }

        // Check file type - only allow JPEG, JPG, and PNG for gigs
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
        if (!allowedTypes.includes(file.type)) {
            return { valid: false, error: 'File type not supported. Only JPEG, JPG, and PNG files are allowed' }
        }

        return { valid: true }
    }

    /**
     * Format file size for display
     */
    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes'

        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    /**
     * Get image dimensions (client-side)
     */
    getImageDimensions(file: File): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image()
            const url = URL.createObjectURL(file)

            img.onload = () => {
                URL.revokeObjectURL(url)
                resolve({ width: img.width, height: img.height })
            }

            img.onerror = () => {
                URL.revokeObjectURL(url)
                reject(new Error('Failed to load image'))
            }

            img.src = url
        })
    }
}

export const imageService = new ImageService()